import { useFeatureFlags } from './chunk-WJJBT3ZR.js';
import { CopyableValue, CopyTextButton, DebugFieldGroups } from './chunk-XKA7GV2U.js';
import { usePlan } from './chunk-MOQIOZ7K.js';
import { useOptionalSession } from './chunk-7QYYSJGW.js';
import { Switch } from './chunk-TU2ZUVHW.js';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './chunk-MRAOULQ3.js';
import { Separator } from './chunk-NCCYLVD3.js';
import { Button } from './chunk-PLP233UC.js';
import * as React from 'react';
import { Bug, X } from 'lucide-react';
import { debugResourceSink, requestTraceSink } from '@authe/fe-commons/api';
import { isDebugEnabled } from '@authe/fe-commons/config';
import { clearAuthCache } from '@authe/fe-commons/session';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

var DebugResourceRegistry = class {
  entries = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Set();
  snapshot = [];
  /** Publish (or, with `null`, withdraw) the record under `key`. */
  publish(key, entry) {
    if (entry === null) {
      if (!this.entries.delete(key)) return;
    } else {
      this.entries.set(key, entry);
    }
    this.snapshot = [...this.entries.values()];
    for (const listener of this.listeners) listener();
  }
  /** Everything published, in publication order. Stable between changes. */
  list = () => this.snapshot;
  subscribe = (listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
};
var DebugResourcesContext = React.createContext(null);
function DebugResourceProvider({
  children,
  registry,
  routeKey
}) {
  const [fallback] = React.useState(() => new DebugResourceRegistry());
  const value = registry ?? fallback;
  React.useEffect(() => {
    if (routeKey === void 0) return;
    debugResourceSink.clear();
  }, [routeKey]);
  return /* @__PURE__ */ jsx(DebugResourcesContext.Provider, { value, children });
}
function useHookResources() {
  const registry = React.useContext(DebugResourcesContext);
  const subscribe = registry?.subscribe ?? noopSubscribe;
  const list = registry?.list ?? emptyList;
  return React.useSyncExternalStore(subscribe, list, list);
}
function useCapturedResources() {
  return React.useSyncExternalStore(
    debugResourceSink.subscribe,
    debugResourceSink.list,
    emptyCaptured
  );
}
function useDebugResources() {
  const hookEntries = useHookResources();
  const captured = useCapturedResources();
  return React.useMemo(
    () => mergeDebugResources(captured, hookEntries),
    [captured, hookEntries]
  );
}
function mergeDebugResources(captured, hookEntries) {
  const byKind = /* @__PURE__ */ new Map();
  for (const entry of captured) byKind.set(entry.resource, withCaptureNote(entry));
  for (const entry of hookEntries) byKind.set(entry.resource, entry);
  return [...byKind.values()];
}
function withCaptureNote(entry) {
  const { path, at, ...rest } = entry;
  return {
    ...rest,
    groups: [
      ...rest.groups,
      {
        key: "captured",
        label: "Captured",
        fields: [
          { key: "request", label: "From request", value: path, kind: "text" },
          {
            key: "at",
            label: "Captured",
            value: Math.floor(at / 1e3),
            kind: "timestamp",
            hint: "The response this was read off. Nothing re-reads it on its own."
          }
        ]
      }
    ]
  };
}
function useDebugResource(entry) {
  const registry = React.useContext(DebugResourcesContext);
  const key = React.useId();
  const enabled = registry !== null && isDebugEnabled();
  const signature = enabled && entry ? entrySignature(entry) : null;
  const [published, setPublished] = React.useState(
    signature === null ? null : entry ?? null
  );
  const [publishedSignature, setPublishedSignature] = React.useState(signature);
  if (signature !== publishedSignature) {
    setPublishedSignature(signature);
    setPublished(signature === null ? null : entry ?? null);
  }
  React.useEffect(() => {
    if (!registry || !published) return void 0;
    registry.publish(key, published);
    return () => registry.publish(key, null);
  }, [registry, published, key]);
}
function resourceLabel(entry) {
  if (entry.label) return entry.label;
  const words = entry.resource.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
function entrySignature(entry) {
  try {
    return JSON.stringify(entry) ?? structuralDigest(entry);
  } catch {
    return structuralDigest(entry);
  }
}
function structuralDigest(entry) {
  const groups = entry.groups.map((g) => `${g.key}:${g.fields.map((f) => f.key).join(",")}`).join("|");
  return `${entry.resource}#${entry.id ?? ""}#${groups}`;
}
var EMPTY = [];
var emptyList = () => EMPTY;
var EMPTY_CAPTURED = [];
var emptyCaptured = () => EMPTY_CAPTURED;
var noopSubscribe = () => () => {
};
function ScopesSection({ scopes }) {
  const [open, setOpen] = React.useState(false);
  if (scopes.length === 0) {
    return /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "No scopes on token." });
  }
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsxs(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "outline",
          className: "text-xs",
          "aria-expanded": open,
          onClick: () => setOpen((o) => !o),
          children: [
            open ? "Hide" : "Show",
            " ",
            scopes.length,
            " scope",
            scopes.length === 1 ? "" : "s"
          ]
        }
      ),
      /* @__PURE__ */ jsx(CopyTextButton, { text: scopes.join("\n") })
    ] }),
    open && /* @__PURE__ */ jsx("ul", { className: "max-h-40 space-y-0.5 overflow-auto rounded-sm border bg-muted/40 p-2 font-mono text-2xs leading-relaxed", children: scopes.map((s) => /* @__PURE__ */ jsx("li", { className: "break-all", children: s }, s)) })
  ] });
}
function PlanSection() {
  const live = usePlan().plan?.id ?? null;
  const minted = useOptionalSession()?.session?.claims?.plan_id ?? null;
  if (!live && !minted) return null;
  const stale = Boolean(live && minted && live !== minted);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-1.5", children: [
    /* @__PURE__ */ jsx(DebugRowView, { label: "plan (live)", value: live ?? "unknown", copyable: Boolean(live) }),
    /* @__PURE__ */ jsx(
      DebugRowView,
      {
        label: "plan (token)",
        value: minted ?? "not stamped",
        copyable: Boolean(minted)
      }
    ),
    stale && /* @__PURE__ */ jsx("p", { className: "text-3xs italic text-muted-foreground", children: "This session predates a plan change; re-minting the token." })
  ] });
}
function RawJwtSection({
  getAccessToken
}) {
  const [token, setToken] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const shown = token !== null;
  const reveal = React.useCallback(() => {
    setLoading(true);
    void getAccessToken().then((t) => setToken(t ?? "")).catch((error) => setToken(`<unavailable: ${String(error)}>`)).finally(() => setLoading(false));
  }, [getAccessToken]);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "outline",
          className: "text-xs",
          disabled: loading,
          "aria-expanded": shown,
          onClick: shown ? () => setToken(null) : reveal,
          children: loading ? "Loading\u2026" : shown ? "Hide raw JWT" : "Show raw JWT"
        }
      ),
      shown && /* @__PURE__ */ jsx(CopyTextButton, { text: token ?? "" })
    ] }),
    shown && /* @__PURE__ */ jsx("pre", { className: "max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-sm border bg-muted/40 p-2 font-mono text-2xs leading-relaxed", children: token || "(no access token)" })
  ] });
}
function DebugRowView({ label, value, copyable }) {
  const asCopyable = copyable && typeof value === "string" && value !== "" && value !== "\u2014";
  return /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3 text-xs", children: [
    /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: label }),
    /* @__PURE__ */ jsx(
      "span",
      {
        className: "max-w-[60%] truncate font-mono text-foreground",
        title: typeof value === "string" ? value : void 0,
        children: asCopyable ? /* @__PURE__ */ jsx(CopyableValue, { value }) : value
      }
    )
  ] });
}
var SESSION_TAB = "session";
function DebugBlock({ title, children }) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Separator, { className: "my-3" }),
    /* @__PURE__ */ jsx("p", { className: "mb-2 text-xs font-medium", children: title }),
    children
  ] });
}
function DebugResourceView({ entry }) {
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    entry.id && /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3 text-xs", children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "id" }),
      /* @__PURE__ */ jsx("span", { className: "max-w-[62%] truncate font-mono", children: /* @__PURE__ */ jsx(CopyableValue, { value: entry.id }) })
    ] }),
    /* @__PURE__ */ jsx(DebugFieldGroups, { groups: entry.groups })
  ] });
}
function DebugPanel({
  title = "Debug",
  rows = [],
  flagNames = [],
  variantFlags = [],
  sections = [],
  scopes,
  getAccessToken,
  onSignOut,
  federatedSignOut = false,
  showClearCache = true,
  enabled,
  defaultOpen = false
}) {
  const flags = useFeatureFlags();
  const [open, setOpen] = React.useState(defaultOpen);
  const resources = useDebugResources();
  const requestTraces = React.useSyncExternalStore(requestTraceSink.subscribe, requestTraceSink.list, requestTraceSink.list);
  const [chosenTab, setChosenTab] = React.useState(null);
  const tabExists = chosenTab === SESSION_TAB || resources.some((r) => r.resource === chosenTab);
  const tab = chosenTab && tabExists ? chosenTab : resources[0]?.resource ?? SESSION_TAB;
  const isOn = enabled ?? isDebugEnabled();
  if (!isOn) return null;
  const toggleFlag = (name, value) => {
    flags.setOverride(name, value);
    window.location.reload();
  };
  const forceVariant = (name, value) => {
    flags.setVariantOverride(name, value);
    window.location.reload();
  };
  if (!open) {
    return /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        size: "icon",
        variant: "outline",
        className: "fixed bottom-4 right-4 z-50 rounded-full shadow-lg",
        onClick: () => setOpen(true),
        "aria-label": "Open debug panel",
        children: /* @__PURE__ */ jsx(Bug, { className: "size-4" })
      }
    );
  }
  const showSession = showClearCache || Boolean(onSignOut);
  const sessionPane = /* @__PURE__ */ jsxs(Fragment, { children: [
    rows.length > 0 && /* @__PURE__ */ jsx("div", { className: "space-y-1.5", children: rows.map((row, i) => /* @__PURE__ */ jsx(DebugRowView, { ...row }, `${row.label}-${i}`)) }),
    scopes && /* @__PURE__ */ jsx(DebugBlock, { title: `Scopes${scopes.length ? ` (${scopes.length})` : ""}`, children: /* @__PURE__ */ jsx(ScopesSection, { scopes }) }),
    /* @__PURE__ */ jsxs(DebugBlock, { title: "Recent API requests", children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Copy a correlation ID to find this request across backend services." }),
      requestTraces.length === 0 && /* @__PURE__ */ jsx("p", { className: "text-xs", children: "No requests captured yet." }),
      /* @__PURE__ */ jsx("div", { className: "max-h-64 shrink-0 space-y-3 overflow-auto", "aria-label": "Request tracing history", children: requestTraces.map((entry, index) => /* @__PURE__ */ jsxs("div", { className: "space-y-1 border-b pb-2 text-xs", children: [
        /* @__PURE__ */ jsxs("div", { className: "break-all", children: [
          entry.status,
          " \xB7 ",
          entry.path,
          " \xB7 ",
          Math.round(entry.durationMs),
          " ms"
        ] }),
        Object.entries(entry.trace).map(([key, value]) => value && /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx("div", { className: "text-muted-foreground", children: key }),
          /* @__PURE__ */ jsx(CopyableValue, { value })
        ] }, key))
      ] }, `${entry.at}-${index}`)) })
    ] }),
    /* @__PURE__ */ jsx(PlanSection, {}),
    getAccessToken && /* @__PURE__ */ jsx(DebugBlock, { title: "Raw JWT", children: /* @__PURE__ */ jsx(RawJwtSection, { getAccessToken }) }),
    showSession && /* @__PURE__ */ jsx(DebugBlock, { title: "Session", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2", children: [
      showClearCache && /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "outline",
          className: "text-xs",
          onClick: () => {
            clearAuthCache();
            window.location.reload();
          },
          children: "Clear local cache"
        }
      ),
      onSignOut && /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "outline",
          className: "text-xs",
          onClick: () => onSignOut({ federated: false }),
          children: "Sign out"
        }
      ),
      onSignOut && federatedSignOut && /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "outline",
          className: "text-xs",
          onClick: () => onSignOut({ federated: true }),
          children: "Federated (IdP too)"
        }
      )
    ] }) }),
    sections.map((section) => /* @__PURE__ */ jsx(DebugBlock, { title: section.title, children: section.content }, section.title)),
    variantFlags.length > 0 && /* @__PURE__ */ jsx(DebugBlock, { title: "Variants", children: /* @__PURE__ */ jsx("div", { className: "space-y-3", children: variantFlags.map(({ name, options }) => {
      const current = flags.variantOf(name);
      const layer = flags.variantLayerOf(name);
      return /* @__PURE__ */ jsxs("div", { className: "space-y-1.5 text-xs", "data-variant-flag": name, children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
          /* @__PURE__ */ jsx("span", { className: "font-mono text-muted-foreground", children: name }),
          /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
            current ?? "\u2014",
            layer ? ` (${layer})` : ""
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-1.5", children: [
          options.map((option) => /* @__PURE__ */ jsx(
            Button,
            {
              type: "button",
              size: "sm",
              variant: current === option ? "brand" : "outline",
              className: "text-xs",
              "aria-pressed": current === option,
              onClick: () => forceVariant(name, option),
              children: option
            },
            option
          )),
          layer === "override" && /* @__PURE__ */ jsx(
            Button,
            {
              type: "button",
              size: "sm",
              variant: "ghost",
              className: "text-xs",
              onClick: () => forceVariant(name, null),
              children: "Reset"
            }
          )
        ] })
      ] }, name);
    }) }) }),
    flagNames.length > 0 && /* @__PURE__ */ jsx(DebugBlock, { title: "Feature flags", children: /* @__PURE__ */ jsx("div", { className: "space-y-2", children: flagNames.map((name) => /* @__PURE__ */ jsxs("label", { className: "flex items-center justify-between text-xs", children: [
      /* @__PURE__ */ jsx("span", { className: "font-mono text-muted-foreground", children: name }),
      /* @__PURE__ */ jsx(Switch, { checked: flags.isEnabled(name), onCheckedChange: (v) => toggleFlag(name, v) })
    ] }, name)) }) })
  ] });
  return /* @__PURE__ */ jsxs("div", { className: "fixed bottom-4 right-4 z-50 flex max-h-[80vh] w-80 flex-col overflow-auto rounded-lg border bg-card p-4 text-card-foreground shadow-xl", children: [
    /* @__PURE__ */ jsxs("div", { className: "mb-3 flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2 text-sm font-semibold", children: [
        /* @__PURE__ */ jsx(Bug, { className: "size-4" }),
        " ",
        title
      ] }),
      /* @__PURE__ */ jsx(Button, { type: "button", size: "icon", variant: "ghost", onClick: () => setOpen(false), "aria-label": "Close", children: /* @__PURE__ */ jsx(X, { className: "size-4" }) })
    ] }),
    resources.length === 0 ? sessionPane : /* @__PURE__ */ jsxs(Tabs, { value: tab, onValueChange: setChosenTab, children: [
      /* @__PURE__ */ jsxs(TabsList, { className: "mb-3 h-auto w-full flex-wrap justify-start", children: [
        /* @__PURE__ */ jsx(TabsTrigger, { value: SESSION_TAB, className: "px-2 py-0.5 text-xs", children: "Session" }),
        resources.map((entry) => /* @__PURE__ */ jsx(TabsTrigger, { value: entry.resource, className: "px-2 py-0.5 text-xs", children: resourceLabel(entry) }, entry.resource))
      ] }),
      /* @__PURE__ */ jsx(TabsContent, { forceMount: true, value: SESSION_TAB, className: "mt-0", children: sessionPane }),
      resources.map((entry) => /* @__PURE__ */ jsx(TabsContent, { forceMount: true, value: entry.resource, className: "mt-0", children: /* @__PURE__ */ jsx(DebugResourceView, { entry }) }, entry.resource))
    ] })
  ] });
}

export { DebugPanel, DebugResourceProvider, DebugResourceRegistry, entrySignature, mergeDebugResources, resourceLabel, useDebugResource, useDebugResources };
//# sourceMappingURL=chunk-GT2OS5OH.js.map
//# sourceMappingURL=chunk-GT2OS5OH.js.map